/**
 * 沉浸式导航栏路由配置
 *
 * 这些路由不在根组件 (RootLayoutClient) 统一处理安全区顶部间距，
 * 而是由页面自己处理，以实现沉浸式导航栏效果。
 *
 * 使用场景：
 * - 页面需要自定义顶部区域样式（如透明导航栏、渐变背景等）
 * - 页面需要图片或内容延伸到状态栏区域
 * - 页面有特殊的滚动交互需求
 *
 * 路由格式说明：
 * - 精确路径: "/crypto/[id]" - 匹配 /crypto/xxx 格式的动态路由
 * - 路径前缀: "/whale-holdings" - 匹配 /whale-holdings 及其子路由
 * - 支持动态参数: [id], [handle], [symbol] 等会被转换为正则匹配
 *
 * @example
 * // 页面内使用安全区
 * import { useSafeAreaInsets } from '@/src/hooks/useSafeAreaInsets';
 * import { useIsImmersiveRoute } from '@/src/hooks/useIsImmersiveRoute';
 *
 * const MyPage = () => {
 *   const insets = useSafeAreaInsets();
 *   const isImmersive = useIsImmersiveRoute();
 *
 *   return (
 *     <div style={{ paddingTop: isImmersive ? insets.top : 0 }}>
 *       {// 页面内容}
 *     </div>
 *   );
 * };
 */
export const IMMERSIVE_ROUTES = [
  // 大佬持仓详情页 - 需要沉浸式头部
  // "/whale-holdings/[whaleId]",
  // 示例：加密货币详情页 - 需要沉浸式头部
  // "/crypto/[id]",
  // 示例：股票详情页 - 需要沉浸式头部
  // "/stock/[symbol]",
  // 示例：用户主页 - 需要沉浸式头部
  // "/aura/[handle]",

  // App 内 Chat 常驻 WebView
  "/app-chat-modal",
  // Goal 详情 — Hero 贴顶，页内浮层导航 + safe area
  "/goal/[id]",
  // Login 页 - 需要沉浸式头部
  "/login",
  "/theme-pack/claimed",
  "/theme-pack/ranking",
  "/theme-pack/connected",
  // V3 onboarding wizard — 页内自理 safe-area，与 QuizStepLayout 主背景同色
  "/onboarding",
  // Welcome 页 - 需要沉浸式头部
  "/welcome",
] as const;

/**
 * 将路由配置转换为正则表达式
 * @param route - 路由配置字符串
 * @returns 正则表达式
 */
const routeToRegex = (route: string): RegExp => {
  // 将动态参数 [xxx] 转换为正则表达式 [^/]+
  const regexPattern = route
    .replace(/\[[\w]+\]/g, "[^/]+") // [id], [handle] 等替换为匹配非斜杠的字符
    .replace(/\//g, "\\/"); // 转义斜杠

  return new RegExp(`^${regexPattern}$`);
};

/**
 * 移除路径中的语言前缀和设备前缀
 * @param pathname - 原始路径
 * @returns 标准化后的路径
 *
 * @example
 * normalizePathname("/en/mobile/crypto/btc") // => "/crypto/btc"
 * normalizePathname("/zh-CN/desktop/whale-holdings/123") // => "/whale-holdings/123"
 */
const normalizePathname = (pathname: string): string => {
  // 移除语言前缀 (如 /en, /zh, /zh-CN 等)
  let normalized = pathname.replace(/^\/[a-z]{2}(?:-[A-Z]{2})?(?=\/|$)/, "");

  // 移除设备前缀 (如 /mobile, /desktop 等)
  normalized = normalized.replace(/^\/(mobile|desktop)(?=\/|$)/, "");

  return normalized || "/";
};

/**
 * 检查路径是否为沉浸式路由
 * @param pathname - 当前路径
 * @returns 如果是沉浸式路由返回 true
 *
 * @example
 * isImmersiveRoute("/en/mobile/crypto/btc") // => true (如果配置了 "/crypto/[id]")
 * isImmersiveRoute("/whale-holdings/123") // => true (如果配置了 "/whale-holdings/[whaleId]")
 */
export const isImmersiveRoute = (pathname: string): boolean => {
  const normalizedPath = normalizePathname(pathname);

  return IMMERSIVE_ROUTES.some((route) => {
    const regex = routeToRegex(route);
    return regex.test(normalizedPath);
  });
};

/**
 * 获取所有沉浸式路由配置
 * @returns 沉浸式路由数组
 */
export const getImmersiveRoutes = (): readonly string[] => {
  return IMMERSIVE_ROUTES;
};
